import { Button, Input, Select, Tooltip } from 'common/components';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import { InfoIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { WorkflowConfigFormData } from 'pages/WorkflowBuilder/AgenticWorkflowBuilder';

export const GenericTagsTab = () => {
    const {
        register,
        getValues,
        control,
        setValue,
        formState: { errors },
    } = useFormContext<WorkflowConfigFormData>();
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'genericTags',
    });

    const handleAddTag = () => {
        const tags = getValues('genericTags') ?? [];
        const maxOrder = Math.max(0, ...tags.map((t) => t.order ?? 0));
        append({ value: '', type: 'ternary', order: maxOrder + 1 });
    };

    const tags = getValues('genericTags') ?? [];
    const sortedIndices = tags
        .map((tag, index) => ({ order: tag.order ?? 0, index }))
        .sort((a, b) => a.order - b.order)
        .map(({ index }) => index);

    return (
        <div className='flex flex-col gap-lg pt-xl'>
            <p className='text-gray-700'>
                Add at least one custom tag. Tags are pulled from call transcripts and used to determine the final
                disposition. You can set a dedicated prompt in prompts tab for each tag to control how it’s evaluated.
            </p>
            <div className='flex justify-end'>
                <Button variant='secondary' onClick={handleAddTag} icon={PlusIcon}>
                    Add Tag
                </Button>
            </div>
            <div className='flex flex-col gap-lg'>
                {sortedIndices.map((index) => (
                    <div key={fields[index].id} className='flex gap-sm' id={`genericTags-${index}`}>
                        <Input
                            label='Tag Name'
                            error={!!errors.genericTags?.[index]?.value}
                            errorText={errors.genericTags?.[index]?.value?.message}
                            {...register(`genericTags.${index}.value`, {
                                required: 'Tag Name is required',
                                validate: (value) => {
                                    const trimmed = value?.trim();
                                    if (!trimmed) return true;
                                    const tags = getValues('genericTags');
                                    const hasDuplicate = (tags || []).some(
                                        (tagData, i) =>
                                            i !== index &&
                                            tagData?.value?.trim().toLowerCase() === trimmed.toLowerCase()
                                    );
                                    return !hasDuplicate || 'A tag with this name already exists';
                                },
                                onChange: (event) => {
                                    setValue(`genericTags.${index}.value`, event.target.value, {
                                        shouldValidate: true,
                                    });
                                },
                            })}
                        />
                        <div className='flex gap-sm w-full'>
                            <Controller
                                control={control}
                                name={`genericTags.${index}.type`}
                                render={({ field }) => (
                                    <Select
                                        label='Tag Type'
                                        options={[
                                            { value: 'ternary', label: 'Ternary' },
                                            { value: 'string', label: 'String' },
                                        ]}
                                        value={field.value}
                                        onChange={(event) => {
                                            field.onChange(event.target.value);
                                        }}
                                    />
                                )}
                            />
                            <Tooltip
                                content={
                                    <div className='flex flex-col gap-sm'>
                                        <span>
                                            After post-processing, as the result of the tag extraction, the answer will
                                            be:
                                        </span>
                                        <span>Ternary: Positive, Negative, or No Answer.</span>
                                        <span>String: Any free-form text.</span>
                                    </div>
                                }
                                position='top'
                            >
                                <div
                                    className='mt-xl mb-auto flex items-center justify-center'
                                    style={{ minHeight: '38px' }}
                                >
                                    <InfoIcon size={14} color='#9b51e0' />
                                </div>
                            </Tooltip>
                        </div>

                        <Button
                            variant='secondary'
                            onClick={() => remove(index)}
                            icon={MinusIcon}
                            disabled={fields.length === 1}
                            style={{ minHeight: '38px', marginTop: '25px', marginBottom: 'auto' }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};
